import { Paper } from '@mui/material';
import Highcharts from 'highcharts';
import { useCallback, useMemo } from 'react';
import { Link as NavLink } from 'react-router-dom';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { currentTeam } from '../../recoil/atoms/currentTeam';
import { endingDate } from '../../recoil/atoms/endingDate';
import { smsMetric } from '../../recoil/atoms/smsMetric';
import { startingDate } from '../../recoil/atoms/startingDate';
import { Role } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { HighchartsReactWithStyles } from '../components/charts/HighchartsReactWithStyles';
import { SmsMetricDropdown } from '../components/dropdowns/SmsMetricDropdown';
import { TeamDropdown } from '../components/dropdowns/TeamDropdown';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { DataTable } from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

/**
 * Create custom hook to return chart/stat data
 */
function useSmsReport() {
  const team = useRecoilValue(currentTeam);
  const currentMetric = useRecoilValue(smsMetric);
  const startDate = useRecoilValue(startingDate);
  const endDate = useRecoilValue(endingDate);
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const smsReports = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.SMS_REPORTS,
      payloadJsonString: JSON.stringify({
        call: endpointEnum.SMS_REPORTS,
        startdate: startDate,
        enddate: endDate,
        timezone:
          businessData.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
    })
  );

  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );

  // create lookup table
  const businessUsersLookup = useMemo(
    () =>
      businessUsers.contents?.response?.data?.result?.reduce(
        (acc, user) => ({
          ...acc,
          [user.userid]: user,
        }),
        {}
      ),
    [businessUsers]
  );

  const smsReportChart = useMemo(
    () =>
      Object.values(smsReports.contents?.response?.data?.stats?.users ?? {})
        .map((smsUserStats) => ({
          id: smsUserStats.userid,
          email: businessUsersLookup?.[smsUserStats.userid]?.email,
          teams: businessUsersLookup?.[smsUserStats.userid]?.teams?.split(','),
          total: smsUserStats.total,
          incoming: smsUserStats.allincoming,
          outgoing: smsUserStats.alloutgoing,
        }))
        .filter(
          (chart) =>
            chart.email && // has email (lookup for userid succeeds)
            (!team.id || // no team set then do not filter
              chart.teams?.includes(String(team.id))) // some teams undefined
        )
        .sort(
          (firstChart, secondChart) =>
            secondChart[currentMetric.toLowerCase()] -
            firstChart[currentMetric.toLowerCase()]
        ),
    [smsReports, team, currentMetric, businessUsers]
  );

  const smsReport = useMemo(
    () => ({
      tableData: smsReportChart, // table data
      chartData: {
        title: {
          text: 'SMS Reporting',
        },
        subtitle: {
          text: currentMetric, // insert metric atom here... for now show totals
        },
        chart: {
          height: 600,
          inverted: true,
          polar: false,
        },
        xAxis: {
          categories: smsReportChart.map((chart) => chart.email), // email on x-axis
          alignTicks: true,
        },
        plotOptions: {
          series: {
            dataSorting: {
              enabled: true,
            },
            animation: {
              duration: 1750,
            },
          },
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            data: smsReportChart.map(
              (chart) => chart[currentMetric.toLowerCase()] // total/incoming/outgoing
            ),
            type: 'column',
            colorByPoint: false,
            showInLegend: false,
          },
        ],
      },
    }),
    [smsReportChart, currentMetric]
  );

  return {
    state:
      smsReports.state === 'loading' || businessUsers.state === 'loading'
        ? 'loading'
        : smsReports.state,
    contents: smsReport,
  };
}

export function SmsReports() {
  // get sms report from custom hook
  const report = useSmsReport();

  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isUser = role === Role.USER;
  const sortModel = [
    {
      field: 'total',
      sort: 'desc',
    },
  ];
  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) =>
        params.value === 'N/A' || isUser ? (
          params.value
        ) : (
          <NavLink to={`/manage/agents/${params.row.id}/edit`}>
            {params.value}
          </NavLink>
        ),
    },
    {
      field: 'incoming',
      headerName: 'Incoming',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'outgoing',
      headerName: 'Outgoing',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'total',
      headerName: 'Total',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
  ];

  const ToolbarComponent = useCallback(
    (props) => <EnhancedTableToolbar {...props} />,
    []
  );
  return (
    <div style={{ maxWidth: '100%' }}>
      <Paper square={true} elevation={0}>
        <TeamDropdown />
        <SmsMetricDropdown />
        <div>
          <HighchartsReactWithStyles
            allowChartUpdate={true}
            highcharts={Highcharts}
            options={report.contents.chartData}
            loading={report.state === 'loading'}
          />
        </div>
        <DataTable
          rows={report.contents?.tableData ?? []}
          columns={columns}
          sortModel={sortModel}
          ToolbarComponent={ToolbarComponent}
          loading={report.state !== 'hasValue'}
        />
      </Paper>
    </div>
  );
}
